import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { TimeTrackingConfigurationUtils } from '../../../classes/entities/TimeTrackingConfiguration/TimeTrackingConfigurationUtils';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';
import { UseSelectEntityItemsUtils } from '../../../hooks/entity/useSelectEntityItems/UseSelectEntityItemsUtils';

export type TimeTrackingConfigurationSelectProps = PropsWithoutChildren<
  Omit<
    Partial<SearchableSelectProps<never>>,
    | 'getKeyOfItem'
    | 'getDisplayTextOfItem'
    | 'items'
    | 'value'
    | 'onValueChanged'
  >
> & {
  value: string | null;
  onValueChanged: (value: string | null) => void;
  userGroupId: string;
};

export const TimeTrackingConfigurationSelect: FunctionComponent<TimeTrackingConfigurationSelectProps> =
  React.memo((props) => {
    const { userGroupId, value, onValueChanged, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const timeTrackingConfigurations = useEntities(
      EntityName.TimeTrackingConfiguration,
      'getByUserGroupId',
      userGroupId
    );
    const timeTrackingConfigurationsWithNullValue = useMemo(() => {
      return [null, ...timeTrackingConfigurations];
    }, [timeTrackingConfigurations]);

    const useSelectEntityItemsReturnValue = useNullableSelectEntityItems({
      value,
      entities: timeTrackingConfigurationsWithNullValue,
      entityNotFoundText: t(
        'components.timeTrackingConfiguration.TimeTrackingConfigurationSelect.notFound'
      ),
      nullValueText: t(
        'components.timeTrackingConfiguration.TimeTrackingConfigurationSelect.nullValue'
      ),
      getDisplayTextOfEntity: TimeTrackingConfigurationUtils.displayTextGetter,
      getKeyOfEntity: TimeTrackingConfigurationUtils.keyGetter,
      getValueOfEntity: UseSelectEntityItemsUtils.entityIdGetter,
      onValueChanged
    });

    return (
      <SearchableSelect
        label={t(
          'components.timeTrackingConfiguration.TimeTrackingConfigurationSelect.label'
        )}
        getDisplayTextOfItem={
          useSelectEntityItemsReturnValue.getDisplayTextOfItem
        }
        getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem}
        items={useSelectEntityItemsReturnValue.items}
        value={useSelectEntityItemsReturnValue.selectedItem}
        onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged}
        {...remainingProps}
      ></SearchableSelect>
    );
  });
